import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';

/**
 * Frontend-relevant feature flags, sourced entirely from the backend
 * (`GET /feature-flags`). The backend is the single source of truth — there is
 * no independent frontend flag. Flags default to the safe "off" value until the
 * backend responds, so gated UI (e.g. call buttons) stays hidden during load.
 */
export interface FeatureFlags {
  voiceCallingEnabled: boolean;
  /** Schedule-first mobile home (day view + Mine/assistant segments + Lists) */
  mobileScheduleHomeEnabled: boolean;
  secretaryWhatsappMacEnabled: boolean;
  secretaryLinkedinEnabled: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  voiceCallingEnabled: false,
  mobileScheduleHomeEnabled: false,
  secretaryWhatsappMacEnabled: false,
  secretaryLinkedinEnabled: false,
};

const FeatureFlagsContext = createContext<FeatureFlags>(DEFAULT_FLAGS);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);

  const loadFlags = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<Partial<FeatureFlags>>('/feature-flags', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlags({
        voiceCallingEnabled: response.data.voiceCallingEnabled ?? false,
        mobileScheduleHomeEnabled:
          response.data.mobileScheduleHomeEnabled ?? false,
        secretaryWhatsappMacEnabled:
          response.data.secretaryWhatsappMacEnabled ?? false,
        secretaryLinkedinEnabled:
          response.data.secretaryLinkedinEnabled ?? false,
      });
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      // Keep safe defaults (everything off) on failure.
      setFlags(DEFAULT_FLAGS);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) {
      loadFlags();
    }
  }, [isAuthenticated, loadFlags]);

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Read the current feature flags. Safe to call anywhere in the tree; returns
 * the "off" defaults when no provider is present.
 */
export const useFeatureFlags = (): FeatureFlags =>
  useContext(FeatureFlagsContext);
